<template>
  <base-layout>
    <base-dashboard :is-loading="isLoading">
      <div class="d-flex flex-column justify-content-between h-100 overflow-hidden">
        <base-wizard-header
          class="flex-shrink-0"
          :active-index="activeIndex"
          :tabs="wizardSteps"
          :title="wizardSteps[activeIndex].title"
          :disable-override="isEditingCampaign && nextButtonActive"
          @update-index="updateActiveIndex"
        >
          <b-btn
            v-show="!this.GET_IS_ADMIN && (!this.campaign.id || this.GET_HAS_CAMPAIGN_ACCESS)"
            :disabled="!this.campaign.id"
            class="btn btn-secondary btn-sm mr-3"
            data-test="campaign-share-button"
            @click="$refs['modalSharingCampaign'].openModal()"
            v-b-tooltip.hover
            title="Share a campaign with third parties"
          >
            Share Campaign
            <i class="fal fa-share-square ml-2" />
          </b-btn>
          <b-btn
            v-show="activeIndex === 2 && (!this.campaign.id || this.GET_HAS_CAMPAIGN_ACCESS)"
            :disabled="this.GET_IS_CAMPAIGN_READONLY || this.sharedCount < 1"
            class="btn btn-secondary btn-sm mr-3"
            data-test="showcase-share-button"
            @click="$refs['modalSharingShowcase'].openModal()"
            v-b-tooltip.hover
            title="Share a showcase of your creative and content with third parties"
          >
            Share Preview
            <i class="fal fa-share-square ml-2" />
          </b-btn>
        </base-wizard-header>
        <div class="flex-grow-1 overflow-auto">
          <b-row
            class="m-0 page-wrapper"
            v-if="activeIndex === 0"
          >
            <b-col
              class="p-5"
              cols="auto"
              lg="8"
            >
              <campaign-info
                ref="campaign-info"
                v-if="activeIndex === 0"
                @updateOrderDetails="updateOrderDetails"
                @setNotValidForNavigation="updateIsNotValidForNavigation"
                @setIsDirty="setIsDirty"
                :key="campaignInfoKey"
              />
            </b-col>
            <b-col
              cols="4"
              class="p-5"
            >
              <campaign-details-card
                ref="CampaignDetailsCard"
              />
            </b-col>
          </b-row>
          <content-info
            v-if="activeIndex === 1"
            ref="content-info"
            @setIsValidForNavigation="setIsValidForNavigation"
            @updateStep="updateStep()"
          />
          <creatives-page
            v-if="activeIndex === 2"
            ref="creatives-page"
            @previous-step="onPreviousStep"
          />
          <campaign-summary
            v-if="activeIndex === 3"
            @next-step="onNextStep"
            @edit-campaign="onEditCampaign"
            @setIsValidForNavigation="setIsValidForNavigation"
          />
          <campaign-invoicing
            v-if="activeIndex === 4"
            :active-index="activeIndex"
          />
        </div>
        <base-wizard-footer
          id="fixed-footer"
          v-if="activeIndex < 4"
          @next-step="onNextStep"
          @previous-step="onPreviousStep"
          :last-disabled="!isValidLastButton"
          :next-disabled="!nextButtonActive"
          :show-back-button="showBackButton"
          :is-loading="isNextButtonLoading"
        />
      </div>
    </base-dashboard>
    <share-modal
      id="modalSharingCampaign"
      ref="modalSharingCampaign"
      :share-type="'CAMPAIGN'"
    />
    <share-modal
      id="modalSharingShowcase"
      ref="modalSharingShowcase"
      :share-type="'SHOWCASE'"
    />
  </base-layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as authenticationTypes from '@/modules/authentication/store/types';

import CampaignInfo from '@/modules/campaigns/components/CampaignInfo/CampaignInfo';
import CampaignSummary from '@/modules/campaigns/components/CampaignSummary';
import CampaignInvoicing from '@/modules/campaigns/components/CampaignInvoicing';
import CampaignDetailsCard from '@/modules/campaigns/components/CampaignDetailsCard';
import ContentInfo from '@/modules/content/components/ContentPage/ContentInfo';
import CreativesPage from '@/modules/content/components/CreativesPage/CreativesPage';

import wizardStepItems from './NewCampaignStepItems';
import ShareModal from '@/modules/share/components/ShareModal';

export default {
  name: 'NewCampaign',
  components: {
    ShareModal,
    CampaignInfo,
    CampaignDetailsCard,
    ContentInfo,
    CreativesPage,
    CampaignSummary,
    CampaignInvoicing,
  },
  data() {
    return {
      isLoading: true,
      campaignId: null,
      newCampaign: {
        title: '',
      },
      clicked: false,
      activeIndex: 0,
      ...wizardStepItems,
      campaignInfoKey: 0,
      isValidLastButton: false,
      isValidNextButton: false,
      isNextButtonLoading: false,
      isDirty: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('account', {
      company: 'companyName',
    }),
    ...mapState('content', [
      'activeCreatives',
      'creative',
      'sharedCount',
    ]),
    ...mapState('content', {
      contentItems: 'newContentItems',
      scrapedContent: 'newScrapedContent',
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
      campaignTypes.GET_CAMPAIGN_PERMISSIONS,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
    ]),
    ...mapGetters('authentication', [
      authenticationTypes.GET_IS_ADMIN,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEMS_WITH_MISSED_CREATIVES,
    ]),
    nextButtonActive() {
      if (this.activeIndex !== 2) return this.isValidNextButton;
      return this.GET_CONTENT_ITEMS_WITH_MISSED_CREATIVES.length === 0;
    },
    showBackButton() {
      return this.activeIndex > 0;
    },
    isEditingCampaign() {
      return !!(this.campaignId);
    },
    creativeKey() {
      return this.creative.paused ? this.activeCreatives[0].creativeKey : this.creative.creativeKey;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.CREATE_ADVERTISER,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_BANNED_WORDS,
      campaignTypes.FETCH_EXISTING_CAMPAIGN,
      campaignTypes.FETCH_ORDER,
      campaignTypes.FETCH_EXISTING_BLACKLIST,
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.UPDATE_SELECTED_ADVERTISER,
      campaignTypes.SET_VIEW_TYPE,
    ]),
    ...mapActions('content', [
      contentTypes.NEW_FETCH_CONTENT_SCRAPES,
      contentTypes.FETCH_EXISTING_CONTENT_SCRAPES,
      contentTypes.FETCH_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_CONTENT_ITEMS,
      contentTypes.FETCH_EXISTING_CREATIVES,
    ]),
    onEstimatedNextStep() {
      const hasCampaignInfo = !!this.$refs['campaign-info'];
      if (hasCampaignInfo) {
        this.$refs['campaign-info'].onSubmit(this.isDirty)
          .then(() => {
            this.activeIndex++;
            this.isDirty = false;
            this.isNextButtonLoading = false;
          }).catch(() => {
            this.isNextButtonLoading = false;
          });
      } else {
        this.activeIndex++;
        this.isDirty = false;
        this.isNextButtonLoading = false;
      }
    },
    onContentNextStep() {
      this.updateStep();
    },
    onPreviousStep() {
      this.activeIndex--;
    },
    onNextStep() {
      if (this.activeIndex === 0) return this.onCampaignInfoNextStep();
      if (this.activeIndex === 1) return this.onContentNextStep();
      this.activeIndex++;
    },
    updateStep() {
      this.isNextButtonLoading = false;
      this.activeIndex++;
    },
    onEditCampaign() {
      this.activeIndex = 0;
    },
    async fetchCampaignPageEntities(campaignId) {
      try {
        await this.FETCH_EXISTING_CONTENT_PROVIDER();
        await this.FETCH_CURRENCIES();
        await this.FETCH_BANNED_WORDS();
        if (typeof campaignId !== 'undefined') {
          this.fetchExistingCampaignEntities(campaignId);
        } else {
          this.isLoading = false;
        }
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    async fetchExistingCampaignEntities(campaignId) {
      try {
        const { advertiserId } = this.$route.params;
        const campaign = await this.FETCH_EXISTING_CAMPAIGN({ advertiserId, campaignId });
        if (typeof campaign.state !== 'undefined' && campaign.state !== 'DRAFT') {
          this.$router.replace('/dashboard');
        }
        this.FETCH_EXISTING_BLACKLIST();
        await this.FETCH_ORDER({ advertiserId, campaignId, setOrder: true });
        this.FETCH_EXISTING_CREATIVES({ advertiserId });
        await this.FETCH_FEED_CONTENT_ITEMS(campaign.feedId);
        if (this.$route.params.hasOwnProperty('targetPage')) this.activeIndex = this.$route.params.targetPage;
        this.isLoading = false;
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    updateOrderDetails(payload) {
      if ((this.$refs.CampaignDetailsCard)) {
        this.$refs.CampaignDetailsCard.setOrderDetails(payload);
      }
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId } = this.$route.params;
      this.campaignId = campaignId;
      this.RESET_CAMPAIGN();
      try {
        const advertisers = await this.FETCH_EXISTING_ADVERTISERS();
        const aid = Number.parseInt(advertiserId, 10) || advertisers[0].id;
        const advertiserSet = advertisers
          .find((advertiser) => advertiser.id === aid);
        if (advertiserId && advertiserSet) {
          await this.UPDATE_SELECTED_ADVERTISER(advertiserSet);
        }
        this.fetchCampaignPageEntities(campaignId);
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    onCampaignInfoNextStep() {
      this.isNextButtonLoading = true;
      this.onEstimatedNextStep();
    },
    updateIsNotValidForNavigation(type, valid) {
      this.setIsValidForNavigation(type, !valid);
    },
    setIsValidForNavigation(type, isValid) {
      if (type === 'last') {
        this.isValidLastButton = isValid;
      } else if (type === 'next') {
        this.isValidNextButton = isValid;
      }
    },
    updateActiveIndex(index) {
      if (this.isDirty) {
        this.warnUserOfUnsavedChanges()
          .then(() => {
            this.activeIndex = index;
            this.isDirty = false;
            this.$refs['campaign-info'].resetPeriods();
          }).catch(() => {});
      } else {
        this.activeIndex = index;
      }
    },
    warnUserOfUnsavedChanges() {
      const h = this.$createElement;
      return new Promise((resolve, reject) => {
        this.$bvModal.msgBoxConfirm(h('p', [
          'You have unsaved changes!',
          h('br'),
          'Press "Proceed" to continue without saving, or "Cancel" to go back and review your changes',
        ]), {
          title: 'Warning',
          size: 'sm',
          buttonSize: 'sm',
          okTitle: 'Proceed',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          cancelVariant: 'tertiary',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
          .then((value) => {
            if (!value) return reject();
            return resolve();
          })
          .catch(() => reject());
      });
    },
    setIsDirty(value) {
      this.isDirty = value;
    },
  },
  created() {
    this.handleCreate();
    this.SET_VIEW_TYPE('Setup');
  },
  watch: {
    $route() {
      this.onEditCampaign();
      this.handleCreate();
      this.$forceUpdate();
    },
  },
};
</script>
<style scoped>
</style>
